"use client";
import { Action, ControlType } from "@/context/AnalyticsProvider";
import { cn } from "@/lib/utils";
import { XIcon } from "lucide-react";
import { useEffect } from "react";
import { createPortal } from "react-dom";
import { GlassButton } from "../GlassButton";
import { ImageViewer } from "./ImageViewer";
import { VideoViewer } from "./VideoViewer";

interface FullScreenMediaProps {
  mediaURL?: string;
  gameTitle?: string;
  handleCloseMedia: () => void;
  isMediaOpen: boolean;
  gameId?: string;
  track?: (event: {
    action: Action;
    extras: Record<string, unknown>;
  }) => void;
}

export const FullScreenMedia = ({
  mediaURL,
  gameTitle,
  handleCloseMedia,
  isMediaOpen,
  gameId,
  track,
}: FullScreenMediaProps) => {
  useEffect(() => {
    if (isMediaOpen && gameId && track) {
      track({
        action: Action.VIEWED,
        extras: {
          id: "fullscreen-media",
          gameId,
          gameName: gameTitle,
          version: "v3",
        },
      });
    }
  }, [isMediaOpen, gameId, gameTitle, track]);

  const handleCloseClick = () => {
    if (gameId && track) {
      track({
        action: Action.PRESSED,
        extras: {
          id: "fullscreen-close",
          control: "FullScreenClose",
          controlType: ControlType.BUTTON,
          gameId,
          gameName: gameTitle,
          version: "v3",
        },
      });
    }
    handleCloseMedia();
  };

  return createPortal(
    <div
      className={cn(
        "fixed inset-0 z-[60] grid place-items-center duration-300 transition-all",
        isMediaOpen
          ? "animate-in fade-in-0 opacity-100 pointer-events-auto bg-black/50 backdrop-blur-2xl"
          : "animate-out fade-out-0 opacity-0 pointer-events-none",
      )}
      aria-label="Close fullscreen media"
    >
      {mediaURL?.endsWith(".webp") ? (
        <ImageViewer
          mediaURL={mediaURL}
          gameTitle={gameTitle}
          handleCloseMedia={handleCloseClick}
        />
      ) : (
        <VideoViewer mediaURL={mediaURL} isMediaOpen={isMediaOpen} />
      )}

      {/* close glass button */}
      <GlassButton
        onClick={handleCloseClick}
        className="absolute top-4 md:top-9 right-4 md:right-12 size-12 md:size-16 bg-black/25"
      >
        <XIcon className="size-5 md:size-6 text-neutral-50" />
      </GlassButton>
    </div>,
    document.body,
  );
};
